import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-528f3cd3"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "card-global" }
const _hoisted_2 = { class: "page-title" }
const _hoisted_3 = {
  key: 0,
  class: "profile-body"
}
const _hoisted_4 = { class: "profile-photo" }
const _hoisted_5 = ["title"]
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 1,
  class: "profile-avatar__placeholder"
}
const _hoisted_8 = { class: "profile-avatar__hint" }
const _hoisted_9 = {
  key: 2,
  class: "profile-avatar__overlay"
}
const _hoisted_10 = { class: "profile-content" }
const _hoisted_11 = { class: "mb-4 text-2xl font-medium" }
const _hoisted_12 = { class: "profile-grid" }
const _hoisted_13 = {
  key: 0,
  class: "profile-field__value"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = {
  key: 0,
  class: "profile-roles"
}
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { class: "profile-actions" }
const _hoisted_19 = ["onSubmit"]
const _hoisted_20 = {
  for: "profile-login",
  class: "input-form-label"
}
const _hoisted_21 = {
  for: "profile-password",
  class: "input-form-label"
}
const _hoisted_22 = { class: "flex gap-3 pt-2" }
const _hoisted_23 = {
  key: 0,
  class: "space-y-4"
}
const _hoisted_24 = { class: "grid grid-cols-2 gap-4 text-sm" }
const _hoisted_25 = { class: "text-gray-400" }
const _hoisted_26 = { class: "text-gray-400" }
const _hoisted_27 = { class: "text-gray-400 mb-2" }
const _hoisted_28 = {
  key: 0,
  class: "flex flex-wrap gap-2"
}
const _hoisted_29 = { key: 1 }

import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { UserIcon } from "@heroicons/vue/outline";
import { UserActions } from "@/store/modules/user/actions";
import { UserGetters } from "@/store/modules/user/getters";
import Button from "@/components/common/Button.vue";
import Modal from "@/components/common/Modal.vue";
import { useToast } from "vue-toastification";
import { t } from "@/i18n";
import { AuthActions, AuthGetters } from "@/store/modules/auth";
import PictureModal from "@/components/user/PictureModal.vue";
import { RoleActions } from "@/store/modules/role/actions";
import { RoleGetters } from "@/store/modules/role/getters";
import Role from "@/types/roles/Role";
import { getCommonStatusLabel, getPermissionLabel } from "@/utils/labels";
import { leaveCurrentAccount } from "@/services/auth/AccountSessionService";

interface ProfileField {
  key: string;
  label: string;
  value?: string;
  fullWidth?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const store = useStore();
const toast = useToast();

const user = computed(() => store.getters[UserGetters.GET_PROFILE]);
const authIdentity = computed(
  () => store.getters[AuthGetters.GET_USER_IDENTITY]
);
const roles = computed(() => store.getters[RoleGetters.GET_ALL] || []);

const fullName = computed(() => {
  if (!user.value) {
    return "";
  }
  return [user.value.surname, user.value.name, user.value.middleName]
    .filter(Boolean)
    .join(" ");
});

const managerName = computed(() => {
  if (!user.value) {
    return "—";
  }
  return (
    user.value.managerName ||
    user.value.coordinatorName ||
    (user.value.coordinatorId != null ? String(user.value.coordinatorId) : "—")
  );
});

const profileFields = computed<ProfileField[]>(() => {
  if (!user.value) {
    return [];
  }

  const display = (value?: string | null) => value || "—";

  return [
    {
      key: "department",
      label: t("profile.department"),
      value: display(user.value.companyName),
    },
    {
      key: "email",
      label: t("profile.email"),
      value: display(user.value.email),
    },
    { key: "job", label: t("profile.job"), value: display(user.value.jobName) },
    { key: "phone", label: t("profile.phone"), value: display(user.value.phone) },
    {
      key: "login",
      label: t("profile.login"),
      value: display(user.value.login),
    },
    { key: "manager", label: t("profile.manager"), value: managerName.value },
    { key: "password", label: t("profile.password"), value: "••••••••" },
    {
      key: "company",
      label: t("profile.company"),
      value: display(user.value.companyName),
    },
    { key: "iin", label: t("profile.iin"), value: display(user.value.iin) },
    { key: "roles", label: t("profile.roles"), fullWidth: true },
  ];
});

const userRoles = computed(() => {
  const explicitRoleIds =
    Array.isArray(user.value?.roleIds) && user.value.roleIds.length
      ? user.value.roleIds
      : Array.isArray(authIdentity.value?.roleIds)
      ? authIdentity.value.roleIds
      : [];

  if (explicitRoleIds.length) {
    return roles.value.filter((role: Role) =>
      explicitRoleIds.includes(role.id)
    );
  }

  const roleNames = Array.isArray(
    (authIdentity.value as { roles?: string[] })?.roles
  )
    ? (authIdentity.value as { roles: string[] }).roles
    : [];

  return roles.value.filter((role: Role) => roleNames.includes(role.name));
});

const selectedRole = ref<Role | null>(null);
const roleModalShow = ref(false);
const passwordModalShow = ref(false);
const roleRefreshing = ref(false);
const switchingAccount = ref(false);

const image = ref("");

onMounted(async () => {
  await updateImage();
  await store.dispatch(RoleActions.FETCH_ALL);
  login.value = user.value?.login;
});

async function updateImage() {
  await store.dispatch(UserActions.PROFILE);

  if (!user.value?.imageGuid) {
    image.value = "";
    return;
  }

  try {
    const profileImage = await store.dispatch(
      UserActions.PROFILE_IMAGE_DOWNLOAD,
      {
        imageGuid: user.value.imageGuid,
      }
    );
    image.value = profileImage.data?.fileData || "";
  } catch {
    image.value = "";
  }
}

async function openRoleDetails(id?: string) {
  if (!id) {
    return;
  }
  await store.dispatch(RoleActions.FETCH_ONE, id);
  selectedRole.value = store.getters[RoleGetters.GET_CURRENT];
  roleModalShow.value = true;
}

async function refreshRole() {
  if (roleRefreshing.value) {
    return;
  }

  roleRefreshing.value = true;
  try {
    await store.dispatch(AuthActions.REFRESH_CONTEXT);
    await Promise.all([
      store.dispatch(UserActions.PROFILE),
      store.dispatch(RoleActions.FETCH_ALL),
    ]);
    toast.success(t("profile.refreshRoleSuccess"));
  } catch {
    toast.error(t("profile.refreshRoleError"));
  } finally {
    roleRefreshing.value = false;
  }
}

async function switchWorkingAccount() {
  if (switchingAccount.value) {
    return;
  }
  switchingAccount.value = true;
  await leaveCurrentAccount(store);
}

const login = ref<string | undefined>("");
const password = ref("");

async function handleSubmit() {
  await store
    .dispatch(UserActions.PROFILE_UPDATE, {
      login: login.value,
      password: password.value,
    })
    .then(async () => {
      const refreshToken = store.getters[AuthGetters.GET_REFRESH_TOKEN];
      await store.dispatch(AuthActions.UPDATE_TOKEN, { refreshToken });
      await store.dispatch(UserActions.PROFILE);
      password.value = "";
      passwordModalShow.value = false;
      toast.success(t("profile.updateSuccess"));
    })
    .catch(() => {
      toast.error(t("profile.updateError"));
    });
}

const pictureModalShow = ref(false);

function pictureModalCloseHandler() {
  pictureModalShow.value = false;
}

async function pictureModalSaveHandler() {
  pictureModalShow.value = false;
  await store.dispatch(UserActions.PROFILE);
  await updateImage();
  toast.success(t("profile.updateSuccess"));
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t("profile.title")), 1),
      (_unref(user))
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("button", {
                type: "button",
                class: "profile-avatar",
                title: image.value ? _ctx.$t('profile.changePhoto') : _ctx.$t('profile.uploadPhoto'),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (pictureModalShow.value = true))
              }, [
                (image.value)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      class: "profile-avatar__image",
                      src: `data:image/png;base64, ${image.value}`,
                      alt: ""
                    }, null, 8, _hoisted_6))
                  : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createVNode(_unref(UserIcon), { class: "profile-avatar__icon" }),
                      _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t("profile.uploadPhoto")), 1)
                    ])),
                (image.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(_ctx.$t("profile.changePhoto")), 1))
                  : _createCommentVNode("", true)
              ], 8, _hoisted_5)
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("h2", _hoisted_11, _toDisplayString(_unref(fullName) || "—"), 1),
              _createElementVNode("dl", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(profileFields), (field) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: field.key,
                    class: _normalizeClass(["profile-field", { 'profile-field--full': field.fullWidth }])
                  }, [
                    _createElementVNode("dt", null, _toDisplayString(field.label), 1),
                    (field.key !== 'roles')
                      ? (_openBlock(), _createElementBlock("dd", _hoisted_13, _toDisplayString(field.value), 1))
                      : (_openBlock(), _createElementBlock("dd", _hoisted_14, [
                          (_unref(userRoles).length)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(userRoles), (role) => {
                                  return (_openBlock(), _createElementBlock("button", {
                                    key: role.id,
                                    type: "button",
                                    class: "tag tag--default cursor-pointer hover:border-primary transition-colors",
                                    onClick: ($event: any) => (openRoleDetails(role.id))
                                  }, _toDisplayString(role.name), 9, _hoisted_16))
                                }), 128))
                              ]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_ctx.$t("process.noData")), 1))
                        ]))
                  ], 2))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createVNode(Button, {
                  type: "button",
                  variant: "default",
                  disabled: roleRefreshing.value,
                  onClick: refreshRole
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(roleRefreshing.value
                ? _ctx.$t("profile.refreshRoleLoading")
                : _ctx.$t("profile.refreshRole")), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]),
                _createVNode(Button, {
                  type: "button",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (passwordModalShow.value = true))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("profile.changePassword")), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_router_link, {
                  to: { name: 'TransferAuthority' },
                  class: "btn btn-default profile-actions__link"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("profile.transferRights")), 1)
                  ]),
                  _: 1
                }),
                _createVNode(Button, {
                  type: "button",
                  variant: "default",
                  disabled: switchingAccount.value,
                  onClick: switchWorkingAccount
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("profile.switchAccount")), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(PictureModal, {
      show: pictureModalShow.value,
      onClose: pictureModalCloseHandler,
      onSave: pictureModalSaveHandler
    }, null, 8, ["show"]),
    _createVNode(Modal, {
      show: passwordModalShow.value,
      title: _ctx.$t('profile.changePasswordTitle'),
      onClose: _cache[5] || (_cache[5] = ($event: any) => (passwordModalShow.value = false))
    }, {
      default: _withCtx(() => [
        _createElementVNode("form", {
          class: "space-y-4",
          onSubmit: _withModifiers(handleSubmit, ["prevent"])
        }, [
          _createElementVNode("div", null, [
            _createElementVNode("label", _hoisted_20, _toDisplayString(_ctx.$t("profile.login")), 1),
            _withDirectives(_createElementVNode("input", {
              id: "profile-login",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((login).value = $event)),
              class: "input-form w-full",
              type: "text",
              name: "login"
            }, null, 512), [
              [
                _vModelText,
                login.value,
                void 0,
                { trim: true }
              ]
            ])
          ]),
          _createElementVNode("div", null, [
            _createElementVNode("label", _hoisted_21, _toDisplayString(_ctx.$t("profile.password")), 1),
            _withDirectives(_createElementVNode("input", {
              id: "profile-password",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((password).value = $event)),
              class: "input-form w-full",
              type: "password",
              name: "password",
              autocomplete: "new-password"
            }, null, 512), [
              [_vModelText, password.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_22, [
            _createVNode(Button, {
              type: "button",
              variant: "default",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (passwordModalShow.value = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("common.cancel")), 1)
              ]),
              _: 1
            }),
            _createVNode(Button, { type: "submit" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("profile.save")), 1)
              ]),
              _: 1
            })
          ])
        ], 40, _hoisted_19)
      ]),
      _: 1
    }, 8, ["show", "title"]),
    _createVNode(Modal, {
      show: roleModalShow.value,
      title: selectedRole.value?.name || _unref(t)('profile.roleDetails'),
      onClose: _cache[6] || (_cache[6] = ($event: any) => (roleModalShow.value = false))
    }, {
      default: _withCtx(() => [
        (selectedRole.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
              _createElementVNode("div", _hoisted_24, [
                _createElementVNode("div", null, [
                  _createElementVNode("div", _hoisted_25, _toDisplayString(_unref(t)("common.status")), 1),
                  _createElementVNode("div", null, _toDisplayString(_unref(getCommonStatusLabel)(selectedRole.value.status)), 1)
                ]),
                _createElementVNode("div", null, [
                  _createElementVNode("div", _hoisted_26, _toDisplayString(_unref(t)("roleForm.adGroup")), 1),
                  _createElementVNode("div", null, _toDisplayString(selectedRole.value.adGroup || "-"), 1)
                ])
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("div", _hoisted_27, _toDisplayString(_unref(t)("roleForm.permissions")), 1),
                (selectedRole.value.permissions?.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedRole.value.permissions, (permission) => {
                        return (_openBlock(), _createElementBlock("span", {
                          key: permission,
                          class: "tag tag--default"
                        }, _toDisplayString(_unref(getPermissionLabel)(permission)), 1))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_29, _toDisplayString(_unref(t)("process.noData")), 1))
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["show", "title"])
  ], 64))
}
}

})